import React from 'react';
import PropTypes from 'prop-types';
import { ContextProvider } from '@front10/helpers/dist/Context/Context';
import { mergeDeep } from '../../../helpers/utils';
import { evaluateObject } from '../../../helpers/evaluators';
import { TrackingProvider, useContainerProps } from '../../../_integrations/everymundo/tracking';
import ErrorBoundary from '../ErrorBoundary';
import { IconProvider } from '@front10/utils';

const defaultTranslate = (key, defaultValue) => defaultValue || key;

const withDeepMergeDefaultProps = (WrappedComponent) => {
  const DeepMergeDefaultProps = (props) => {
    const { flavor } = props;
    let flavorProps = {};
    if (flavor && WrappedComponent.flavors && WrappedComponent.flavors[flavor]) {
      const flavorObj = WrappedComponent.flavors ? { ...WrappedComponent.flavors[flavor] } : {};
      flavorProps = mergeDeep({}, flavorObj);
    }
    const myDefaults = mergeDeep({}, WrappedComponent.defaultProps);
    const propAndFlavor = mergeDeep(myDefaults, flavorProps);
    const myDefaultsWithFlavorProps = mergeDeep(propAndFlavor, props);

    const {
      integrationSettings,
      prePopulation,
      translate,
      direction,
      localizationData,
      theme,
      enableErrorBoundary,
      iconProvider = {
        config: {},
        icons: []
      }
    } = myDefaultsWithFlavorProps;

    // patch to solve EM issue with the evaluation of endpoints
    if (prePopulation.airlineIataCode) {
      prePopulation.airlineCode = prePopulation.airlineIataCode;
    }

    // here any transformation... evaluation, all common stuff to all class containers

    const evaluatedIntegrationSettings = evaluateObject(integrationSettings, prePopulation);

    myDefaultsWithFlavorProps.integrationSettings = evaluatedIntegrationSettings;

    const trackingProps = useContainerProps(myDefaultsWithFlavorProps);

    return (
      <ContextProvider
        translate={translate}
        direction={direction}
        localizationData={localizationData}
        theme={theme}
      >
        <IconProvider iconProvider={iconProvider}>
          <TrackingProvider value={trackingProps}>
            {enableErrorBoundary ? (
              <ErrorBoundary>
                <WrappedComponent {...myDefaultsWithFlavorProps} />
              </ErrorBoundary>
            ) : (
              <WrappedComponent {...myDefaultsWithFlavorProps} />
            )}
          </TrackingProvider>
        </IconProvider>
      </ContextProvider>
    );
  };

  DeepMergeDefaultProps.propTypes = {
    flavor: PropTypes.string,
    /**
     * LocalizationData via moment to be used as a date translator
     * https://github.com/moment/moment/blob/develop/src/locale/es.js
     */
    localizationData: PropTypes.objectOf(PropTypes.any),
    /**
     * Component Metadata
     */
    meta: PropTypes.objectOf(PropTypes.any),
    /**
     * Wether to use the default error boundary to catch UI errors.
     * Set this to false if you want to implement your custom error
     * handling.
     */
    enableErrorBoundary: PropTypes.bool
  };

  DeepMergeDefaultProps.defaultProps = {
    flavor: null,
    localizationData: null,
    meta: {
      moduleId: 'em-cmp-lib-airmodules',
      name: 'TO_BE_OVERWRITE_BY_THE_SPECIFIC_CONTAINER',
      version: 'NO_NEEDED'
    },
    pageContext: {
      deviceCategory: 'DESKTOP',
      siteEdition: 'en_HK'
    },
    enableErrorBoundary: true,
    translate: defaultTranslate,
    direction: 'ltr'
  };

  return DeepMergeDefaultProps;
};
export default withDeepMergeDefaultProps;
